import { COMPOSE_EMAIL_TOOL } from '@realadvisor/common-types';
import type { ChatStatus } from 'ai';

import type { MessagePart } from './ChatMessage';

// Pure rules for the panel's header views (chat / history / files) while a
// reply streams. Importable from node tests.

type Messages = ReadonlyArray<{
  role: string;
  parts?: ReadonlyArray<MessagePart>;
}>;

const isStreaming = (status: ChatStatus): boolean =>
  status === 'submitted' || status === 'streaming';

// Switching chats mid-reply is what loses work (it halts the stream), so New
// chat and History wait. Files only lists and opens the user's files: the
// useChat state stays in ChatPanelInner, so the reply keeps streaming.
export const headerActionsEnabled = (
  status: ChatStatus,
): { newChat: boolean; history: boolean; files: boolean } => ({
  newChat: !isStreaming(status),
  history: !isStreaming(status),
  files: true,
});

// Changes whenever a tool call of the conversation gets its result, so an
// open Files list refetches and shows files saved during the current reply.
export const settledToolResultCount = (messages: Messages): number =>
  messages.reduce(
    (count, message) =>
      count +
      (message.parts ?? []).filter(part => part.state === 'output-available')
        .length,
    0,
  );

// Something in the last reply waits for the user (an approval card, or an
// email to send or discard). Shown as a dot on Files' Back button, so it is
// not missed while the chat view is hidden.
export const waitsForUser = (messages: Messages): boolean => {
  const last = messages[messages.length - 1];
  if (!last || last.role !== 'assistant') {
    return false;
  }
  return (last.parts ?? []).some(
    part =>
      part.state === 'approval-requested' ||
      (part.type === `tool-${COMPOSE_EMAIL_TOOL}` &&
        part.state === 'input-available'),
  );
};
